import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowLeft, ArrowRight, Loader2, ChevronDown, ChevronRight,
  Table2, BookOpen, Key, Pencil,
} from 'lucide-react';
import { onboardingApi } from '../../services/api';
import type { OnboardingState, DictionaryResult, TableDictionary } from '../../types';

interface Props {
  state: OnboardingState;
  setDictionaryResult: (r: DictionaryResult) => void;
  onNext: () => void;
  onBack: () => void;
}

const DictionaryStep: React.FC<Props> = ({ state, setDictionaryResult, onNext, onBack }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Empty set = every card expanded by default; users collapse individually.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [editingCell, setEditingCell] = useState<string | null>(null);
  const started = useRef(false);

  /* Auto-start dictionary generation when step is reached */
  useEffect(() => {
    if (started.current || state.dictionaryResult) return;
    started.current = true;

    const runDictionary = async () => {
      setLoading(true);
      setError('');
      try {
        const resp = await onboardingApi.generateDictionary({
          connection: state.connection,
          schema: state.selectedSchema,
          tables: state.selectedTables.map((t) => t.table_name),
          top_k_rows: 5,
        });
        setDictionaryResult(resp.data.payload);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Dictionary generation failed');
      } finally {
        setLoading(false);
      }
    };

    runDictionary();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const result = state.dictionaryResult;

  /* Allow user to edit a description inline */
  const handleDescriptionEdit = (tableName: string, colName: string, newDesc: string) => {
    if (!result) return;
    const updated = {
      ...result,
      tables: result.tables.map((t) => {
        if (t.table_name !== tableName) return t;
        return {
          ...t,
          columns: t.columns.map((c) =>
            c.name === colName ? { ...c, description: newDesc } : c,
          ),
        };
      }),
    };
    setDictionaryResult(updated);
    setEditingCell(null);
  };

  return (
    <div>
      <h2 className="text-lg font-semibold text-cargo-text mb-1">Generate Dictionary</h2>
      <p className="text-cargo-muted text-sm mb-4">
        Auto generated column descriptions for {state.selectedTables.length} selected table{state.selectedTables.length !== 1 ? 's' : ''} in <strong>{state.selectedSchema}</strong>.
        You can edit any description before proceeding.
      </p>

      {/* Loading */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-16 gap-4">
          <Loader2 className="w-8 h-8 text-cargo-accent animate-spin" />
          <p className="text-cargo-muted text-sm">
            Generating descriptions for selected tables...
          </p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-cargo-error/10 border border-cargo-error/30 rounded-lg p-4 text-cargo-error text-sm mb-4">
          {error}
          <button
            onClick={() => { started.current = false; setError(''); }}
            className="ml-3 underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="space-y-3">
          <div className="flex gap-4 mb-4">
            <div className="bg-cargo-surface border border-cargo-border rounded-lg px-4 py-2">
              <p className="text-xs text-cargo-muted">Tables</p>
              <p className="text-lg font-semibold text-cargo-text">{result.tables.length}</p>
            </div>
            <div className="bg-cargo-surface border border-cargo-border rounded-lg px-4 py-2">
              <p className="text-xs text-cargo-muted">Columns Described</p>
              <p className="text-lg font-semibold text-cargo-text">
                {result.tables.reduce((s, t) => s + t.columns.filter((c) => c.description).length, 0)}
              </p>
            </div>
          </div>

          {result.tables.map((table) => (
            <DictTableCard
              key={table.table_name}
              table={table}
              expanded={!collapsed.has(table.table_name)}
              onToggle={() =>
                setCollapsed((prev) => {
                  const next = new Set(prev);
                  if (next.has(table.table_name)) next.delete(table.table_name);
                  else next.add(table.table_name);
                  return next;
                })
              }
              editingCell={editingCell}
              setEditingCell={setEditingCell}
              onEditDescription={handleDescriptionEdit}
            />
          ))}
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between mt-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-cargo-muted hover:text-cargo-text transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button
          onClick={onNext}
          disabled={!result}
          className="flex items-center gap-2 bg-cargo-accent hover:bg-cargo-accent-hover text-white
                     font-medium py-2.5 px-5 rounded-lg transition-colors disabled:opacity-30"
        >
          Next <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

/* ─── Sub-components ─── */

function DictTableCard({
  table,
  expanded,
  onToggle,
  editingCell,
  setEditingCell,
  onEditDescription,
}: {
  table: TableDictionary;
  expanded: boolean;
  onToggle: () => void;
  editingCell: string | null;
  setEditingCell: (k: string | null) => void;
  onEditDescription: (table: string, col: string, desc: string) => void;
}) {
  return (
    <div className="bg-cargo-surface border border-cargo-border rounded-xl overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-cargo-hover/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Table2 className="w-4 h-4 text-cargo-accent" />
          <span className="font-medium text-cargo-text">{table.table_name}</span>
          <span className="text-xs text-cargo-muted">{table.columns.length} columns</span>
        </div>
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-cargo-muted" />
        ) : (
          <ChevronRight className="w-4 h-4 text-cargo-muted" />
        )}
      </button>

      {expanded && (
        <div className="border-t border-cargo-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-cargo-panel text-cargo-muted text-xs uppercase tracking-wider">
                <th className="text-left px-4 py-2 w-1/5">Column</th>
                <th className="text-left px-4 py-2 w-1/6">Type</th>
                <th className="text-center px-4 py-2 w-12">PK</th>
                <th className="text-left px-4 py-2">Description</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {table.columns.map((col) => {
                const cellKey = `${table.table_name}.${col.name}`;
                const isEditing = editingCell === cellKey;
                return (
                  <tr
                    key={col.name}
                    className="border-t border-cargo-border hover:bg-cargo-hover/20"
                  >
                    <td className="px-4 py-2 font-medium text-cargo-text">{col.name}</td>
                    <td className="px-4 py-2 text-cargo-muted">{col.data_type}</td>
                    <td className="px-4 py-2 text-center">
                      {col.primary_key && <Key className="w-3 h-3 text-cargo-warning mx-auto" />}
                    </td>
                    <td className="px-4 py-2">
                      {isEditing ? (
                        <input
                          autoFocus
                          defaultValue={col.description}
                          onBlur={(e) =>
                            onEditDescription(table.table_name, col.name, e.target.value)
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              onEditDescription(
                                table.table_name,
                                col.name,
                                (e.target as HTMLInputElement).value,
                              );
                            }
                            if (e.key === 'Escape') setEditingCell(null);
                          }}
                          className="w-full px-2 py-1 text-sm rounded border border-cargo-accent
                                     bg-white text-cargo-text outline-none"
                        />
                      ) : (
                        <span className="text-cargo-text">
                          {col.description || (
                            <span className="text-cargo-muted italic">No description</span>
                          )}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      {!isEditing && (
                        <button
                          onClick={() => setEditingCell(cellKey)}
                          className="text-cargo-muted hover:text-cargo-accent transition-colors"
                          title="Edit description"
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default DictionaryStep;
